import { Link } from "react-router-dom";
import { Mail, MapPin, Sparkles, Building2, ClipboardCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

export default function EventsPage() {
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-pink-50 to-white" data-testid="events-page">
      <SEO 
        title="Live Cotton Candy Spinning for Venues & Corporate Events"
        description="Live cotton candy spinning for select corporate and venue events in Las Vegas and Boise. Offered on a limited basis and accepted by request. Primary focus: wholesale cotton candy."
        keywords="live cotton candy Las Vegas, live cotton candy Boise, corporate event cotton candy, venue activation cotton candy, wholesale cotton candy"
        canonical="/events"
      />
      
      {/* Hero */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-[#FF9EAA] text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
            LIVE SPINNING — BY REQUEST
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-6">
            Live Cotton Candy <span className="gradient-text">Spinning</span>
          </h1>
          <p className="text-lg md:text-xl text-[#888888] mb-6 leading-relaxed max-w-2xl mx-auto">
            Fresh spun cotton candy on site for select corporate, venue, and brand events in Las Vegas and Boise.
          </p>
          <p className="text-base text-[#888888] max-w-2xl mx-auto" data-testid="events-acceptance-note">
            Live spinning is offered on a limited basis alongside our wholesale program. Events are <span className="font-semibold text-[#4A4A4A]">accepted by request</span>—not booked on demand—based on availability and fit.
          </p>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-[#FF9EAA] flex items-center justify-center mx-auto mb-6">
                <Sparkles className="text-white" size={32} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mb-4">What to Expect</h2>
              <p className="text-lg text-[#888888] max-w-xl mx-auto">
                A clean, professional setup spinning fresh cotton candy for your guests. Simple and elevated—no novelty shapes or performance act.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-pink-50 rounded-2xl p-6 text-center">
                <Building2 className="mx-auto mb-3 text-[#FF9EAA]" size={28} />
                <h3 className="font-bold text-[#4A4A4A] mb-1">Best Fit</h3>
                <p className="text-sm text-[#888888]">Corporate events, venue activations, brand launches, and resort programming</p>
              </div>
              <div className="bg-purple-50 rounded-2xl p-6 text-center">
                <Sparkles className="mx-auto mb-3 text-purple-500" size={28} />
                <h3 className="font-bold text-[#4A4A4A] mb-1">UV Option</h3>
                <p className="text-sm text-[#888888]">Non-toxic, naturally UV-activated Birthday Cake available for blacklight venues</p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-6 text-center">
                <ClipboardCheck className="mx-auto mb-3 text-blue-500" size={28} />
                <h3 className="font-bold text-[#4A4A4A] mb-1">Request Review</h3>
                <p className="text-sm text-[#888888]">Submit details and we&apos;ll confirm whether we can accept your event</p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row justify-center gap-6 mb-10">
              <div className="flex items-center justify-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-6 py-4 rounded-2xl">
                <MapPin size={24} />
                <span className="font-semibold text-lg">Las Vegas, Nevada</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-pink-50 border border-pink-200 text-pink-800 px-6 py-4 rounded-2xl">
                <MapPin size={24} />
                <span className="font-semibold text-lg">Boise, Idaho</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 mb-8 text-center">
              <p className="text-[#4A4A4A] font-medium text-lg mb-4">Send event details (date, location, guest count, hours) to:</p>
              <a 
                href="mailto:shea@cottoncandygirl.co"
                className="inline-flex items-center gap-3 text-2xl md:text-3xl font-bold text-[#FF9EAA] hover:text-[#FF8FAB] transition-colors"
                data-testid="events-email-link"
              >
                <Mail size={32} />
                shea@cottoncandygirl.co
              </a>
            </div>
            
            <div className="text-center">
              <a href="mailto:shea@cottoncandygirl.co?subject=Live%20Spinning%20Request">
                <Button 
                  className="bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all btn-scale"
                  data-testid="contact-event-btn"
                >
                  <Mail className="mr-2" size={20} />
                  Submit a Live Spinning Request
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale Redirect */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#4A4A4A] mb-4">Looking to Stock Cotton Candy?</h2>
          <p className="text-lg text-[#888888] mb-8">
            Our primary focus is wholesale. Shelf-ready 32oz containers, private-label options, and monthly delivery.
          </p>
          <Link to="/wholesale">
            <Button 
              variant="outline"
              className="border-2 border-[#FF9EAA] text-[#E07A8A] hover:bg-pink-50 rounded-full px-10 py-6 text-lg font-semibold transition-all"
              data-testid="events-wholesale-btn"
            >
              <Building2 className="mr-2" size={20} />
              View Wholesale Program
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
